import React, { useState } from 'react';
import api from '../../../config/axios';
import { toast } from 'react-toastify';
import { diasDesdeFecha } from '../../../utils/date';

// Textos propuestos para el envío (piloto manual — nada se envía desde aquí).
// Sin descuento ni precio: se vende conveniencia (decisión KD7 del plan moto-llena).
// La última frase gestiona el rebalse si la vuelta se llena (D-S3).
export const MOTO_LLENA_PUSH_TEXT = {
    title: 'Aguaman está en tu zona 💧',
    body: 'Estamos pasando por tu sector ahorita. Pide tu recarga y aprovechamos la vuelta: te llega hoy mismo.',
};

export const MOTO_LLENA_WHATSAPP_TEXT = (nombre) =>
    `Hola ${nombre} 👋 Estamos entregando agua en tu zona en estos momentos. ` +
    `Si necesitas recarga, respóndeme o pide por la app y aprovechamos la vuelta de una vez. ` +
    `Si la vuelta se llena, te la llevamos igual hoy. 🚰`;

const ChannelBadge = ({ channel }) => {
    if (channel === 'push') {
        return (
            <span className="badge badge-pill" style={{ backgroundColor: '#00a4d3', color: 'white' }}>
                Push
            </span>
        );
    }
    return (
        <span className="badge badge-pill" style={{ backgroundColor: '#25D366', color: 'white' }}>
            WhatsApp
        </span>
    );
};

const formatLastPurchase = (lastPurchaseAt) => {
    if (!lastPurchaseAt) return 'sin pedidos';
    const dias = diasDesdeFecha(lastPurchaseAt);
    if (dias <= 0) return 'hoy';
    if (dias === 1) return 'ayer';
    if (dias < 30) return `hace ${dias} días`;
    const meses = Math.floor(dias / 30);
    return `hace ${meses} ${meses === 1 ? 'mes' : 'meses'}`;
};

const formatQuantity = (quantity) => {
    if (quantity === null || quantity === undefined) return '—';
    return `${quantity} botellón${quantity === 1 ? '' : 'es'}`;
};

const formatDistance = (meters) => {
    if (meters < 1000) return `${meters} m`;
    return `${(meters / 1000).toFixed(1)} km`;
};

const firstName = (name) => (name || '').trim().split(' ')[0] || '';

const EXCLUDED_LABELS = {
    same_client: 'mismo cliente del pedido',
    out_of_radius: 'fuera del radio',
    purchased_today: 'ya pidió hoy',
    no_channel: 'sin canal alcanzable',
    no_coords: 'sin coordenadas',
    inactive_30d: 'sin pedir en 30 días',
    over_limit: 'más allá del top 5',
    trigger_no_coords: 'pedido sin GPS',
};

/**
 * Moto llena — consulta MANUAL de candidatos cercanos al pedido (≤1.5km).
 * Componente de pruebas para ADMIN: maneja su propio estado y SOLO fetchea
 * cuando se pulsa el botón (no al montar ni al abrir el detalle).
 */
const MotoLlenaCandidates = (props) => {
    const { purchaseId } = props;
    const [phase, setPhase] = useState('idle'); // idle | loading | done | error
    const [data, setData] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [showTexts, setShowTexts] = useState(false);
    const [sending, setSending] = useState({}); // clientId -> bool
    const [confirmingPush, setConfirmingPush] = useState(null); // clientId pendiente de 2º toque

    const fetchCandidates = async () => {
        setPhase('loading');
        setErrorMsg('');
        try {
            const response = await api.get(
                `/purchase/${purchaseId}/moto-llena-candidates?t=${Date.now()}`,
            );
            setData(response.data.data);
            setPhase('done');
        } catch (error) {
            const msg =
                error.response && error.response.data && error.response.data.errors
                    ? typeof error.response.data.errors[0].msg === 'string'
                        ? error.response.data.errors[0].msg
                        : 'Error consultando candidatos'
                    : 'Error de conexión consultando candidatos';
            setErrorMsg(msg);
            setPhase('error');
        }
    };

    const registerNotified = async (clientId, channel) => {
        await api.post(`/purchase/${purchaseId}/moto-llena-notify`, { clientId, channel });
    };

    // WhatsApp: abre WhatsApp Business (app/web) con el mensaje precargado —
    // David lo envía manualmente. El click registra la notificación (cap semanal).
    const handleSendWhatsapp = async (candidate) => {
        const message = MOTO_LLENA_WHATSAPP_TEXT(firstName(candidate.name));
        const url = `https://wa.me/${candidate.phone}?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
        setSending((prev) => ({ ...prev, [candidate.clientId]: true }));
        try {
            await registerNotified(candidate.clientId, 'whatsapp');
            toast.success(`WhatsApp abierto para ${firstName(candidate.name)} — notificación registrada`);
            await fetchCandidates();
        } catch (error) {
            toast.error('No se pudo registrar la notificación — vuelve a intentar');
        } finally {
            setSending((prev) => ({ ...prev, [candidate.clientId]: false }));
        }
    };

    // Push: envío directo por el endpoint de notificación al cliente.
    // Confirmación en 2 toques porque sale sin revisión humana.
    const handleSendPush = async (candidate) => {
        if (confirmingPush !== candidate.clientId) {
            setConfirmingPush(candidate.clientId);
            setTimeout(() => {
                setConfirmingPush((current) =>
                    current === candidate.clientId ? null : current,
                );
            }, 3000);
            return;
        }
        setConfirmingPush(null);
        setSending((prev) => ({ ...prev, [candidate.clientId]: true }));
        try {
            await api.post(`/client/${candidate.clientId}/notification`, {
                title: MOTO_LLENA_PUSH_TEXT.title,
                body: MOTO_LLENA_PUSH_TEXT.body,
                data: { view: 'Home' },
            });
            await registerNotified(candidate.clientId, 'push');
            toast.success(`Push enviado a ${firstName(candidate.name)}`);
            await fetchCandidates();
        } catch (error) {
            toast.error('Error enviando el push — inténtalo de nuevo');
        } finally {
            setSending((prev) => ({ ...prev, [candidate.clientId]: false }));
        }
    };

    const renderExcluded = () => {
        if (!data || !data.excluded || Object.keys(data.excluded).length === 0) return null;
        const parts = Object.entries(data.excluded).map(([reason, count]) => {
            const label = EXCLUDED_LABELS[reason] || reason;
            return `${count} ${label}`;
        });
        return (
            <p className="text-muted mb-0" style={{ fontSize: '0.85em' }}>
                Excluidos: {parts.join(' · ')}
            </p>
        );
    };

    return (
        <div className="card">
            <div className="card-body">
                <h4 className="text-primary">Moto llena (pruebas)</h4>

                {phase === 'idle' && (
                    <div>
                        <p className="text-muted" style={{ fontSize: '0.9em' }}>
                            Clientes con dirección a menos de 1.5 km de este pedido, para
                            llenar la vuelta de la moto. Nada se envía automáticamente.
                        </p>
                        <button className="btn btn-sm btn-info" onClick={fetchCandidates}>
                            Buscar candidatos cercanos
                        </button>
                    </div>
                )}

                {phase === 'loading' && (
                    <div className="text-center">
                        <div className="spinner-border spinner-border-sm text-info" role="status" />
                        <span className="ml-2">Buscando candidatos…</span>
                    </div>
                )}

                {phase === 'error' && (
                    <div>
                        <p className="text-danger mb-2" style={{ fontSize: '0.9em' }}>
                            {errorMsg}
                        </p>
                        <button className="btn btn-sm btn-info" onClick={fetchCandidates}>
                            Reintentar
                        </button>
                    </div>
                )}

                {phase === 'done' && data && (
                    <div>
                        {data.candidates.length === 0 ? (
                            <p className="text-muted mb-1">
                                No hay candidatos en {data.radiusKm} km alrededor de este pedido.
                            </p>
                        ) : (
                            <div className="list-group mb-2">
                                {data.candidates.map((candidate, index) => (
                                    <div
                                        key={`motoLlena-${candidate.clientId}`}
                                        className="list-group-item flex-column"
                                        style={{
                                            borderLeft:
                                                index === 0
                                                    ? '4px solid #00a4d3'
                                                    : undefined,
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start">
                                            <strong>{candidate.name}</strong>
                                            <ChannelBadge channel={candidate.channel} />
                                        </div>
                                        <div
                                            className="d-flex justify-content-between"
                                            style={{ fontSize: '0.85em' }}
                                        >
                                            <span className="text-muted">
                                                Último pedido: {formatLastPurchase(candidate.lastPurchaseAt)}
                                            </span>
                                            <span className="text-muted">
                                                {formatDistance(candidate.distanceMeters)}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between" style={{ fontSize: '0.85em' }}>
                                            <span>Pide: {formatQuantity(candidate.lastPurchaseQuantity)}</span>
                                        </div>
                                        <div className="d-flex flex-wrap justify-content-end mt-1">
                                            {candidate.channel === 'whatsapp' ? (
                                                <button
                                                    className="btn btn-xs mb-1"
                                                    style={{ backgroundColor: '#25D366', color: 'white' }}
                                                    disabled={sending[candidate.clientId]}
                                                    onClick={() => handleSendWhatsapp(candidate)}
                                                >
                                                    {sending[candidate.clientId]
                                                        ? 'Registrando…'
                                                        : 'Enviar WhatsApp'}
                                                </button>
                                            ) : (
                                                <button
                                                    className="btn btn-xs mb-1"
                                                    style={{
                                                        backgroundColor: confirmingPush === candidate.clientId
                                                            ? '#dc3545'
                                                            : '#00a4d3',
                                                        color: 'white',
                                                    }}
                                                    disabled={sending[candidate.clientId]}
                                                    onClick={() => handleSendPush(candidate)}
                                                >
                                                    {sending[candidate.clientId]
                                                        ? 'Enviando…'
                                                        : confirmingPush === candidate.clientId
                                                            ? '¿Confirmar envío?'
                                                            : 'Enviar push'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {data.recentlyNotified && data.recentlyNotified.length > 0 && (
                            <div className="mb-2">
                                <p className="text-muted mb-1" style={{ fontSize: '0.8em' }}>
                                    <strong>Notificados esta semana</strong> (espera de 7 días):
                                </p>
                                {data.recentlyNotified.map((client) => (
                                    <p
                                        key={`notified-${client.clientId}`}
                                        className="text-muted mb-1"
                                        style={{ fontSize: '0.8em', textDecoration: 'line-through' }}
                                    >
                                        {client.name} ·{' '}
                                        {client.notifiedAt
                                            ? formatLastPurchase(client.notifiedAt)
                                            : ''}{' '}
                                        por {client.channel === 'push' ? 'push' : 'WhatsApp'}
                                        {client.distanceMeters !== null
                                            ? ` · ${formatDistance(client.distanceMeters)}`
                                            : ''}
                                    </p>
                                ))}
                            </div>
                        )}

                        {renderExcluded()}

                        <div className="d-flex flex-wrap justify-content-end mt-2">
                            <button
                                className="btn btn-xs btn-outline-secondary mr-2 mb-1"
                                onClick={() => setShowTexts(!showTexts)}
                            >
                                {showTexts ? 'Ocultar textos' : 'Ver textos a enviar'}
                            </button>
                            <button className="btn btn-xs btn-info mb-1" onClick={fetchCandidates}>
                                Actualizar
                            </button>
                        </div>

                        {showTexts && (
                            <div className="mt-2">
                                <div className="form-group">
                                    <label style={{ fontSize: '0.85em' }} className="mb-1">
                                        <strong>Notificación push</strong> (candidatos con badge Push)
                                    </label>
                                    <p
                                        className="mb-2"
                                        style={{
                                            fontSize: '0.85em',
                                            fontStyle: 'italic',
                                            backgroundColor: 'rgba(0, 164, 211, 0.08)',
                                            padding: '0.5rem',
                                            borderRadius: '0.25rem',
                                        }}
                                    >
                                        <strong>{MOTO_LLENA_PUSH_TEXT.title}</strong>
                                        <br />
                                        {MOTO_LLENA_PUSH_TEXT.body}
                                    </p>
                                </div>
                                <div className="form-group mb-0">
                                    <label style={{ fontSize: '0.85em' }} className="mb-1">
                                        <strong>WhatsApp</strong> (candidatos con badge WhatsApp)
                                    </label>
                                    <p
                                        className="mb-0"
                                        style={{
                                            fontSize: '0.85em',
                                            fontStyle: 'italic',
                                            backgroundColor: 'rgba(37, 211, 102, 0.08)',
                                            padding: '0.5rem',
                                            borderRadius: '0.25rem',
                                        }}
                                    >
                                        {MOTO_LLENA_WHATSAPP_TEXT('{nombre del cliente}')}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MotoLlenaCandidates;
